import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AssistantAction, AssistantBridge, AssistantStatus, AuthHeadersFn, GhostFeature, ItemLayer,
  LayerName, MapContextSnapshot, ProposalKind, Scope, SelectableType,
} from './assistantTypes';
import { applyAction as applyActionPure, ActionTarget, ITEM_LAYERS } from './applyAction';
import { fetchStatus } from './assistantApi';

/** Item types the backend calls `task_set`; App.tsx's search list calls it `task-set`. */
type SearchItemType = 'point' | 'line' | 'polygon' | 'sensor' | 'incident' | 'mission' | 'task-set';

interface SearchableItem {
  id: number | string;
  name: string;
  type: SearchItemType;
  latitude?: number;
  longitude?: number;
  coordinates?: number[][];
}

type SetOfIds = React.Dispatch<React.SetStateAction<Set<number>>>;

export interface AssistantBridgeOptions {
  getAuthHeaders: AuthHeadersFn;
  mapRef: React.MutableRefObject<unknown>;
  scope: Scope;
  mapStyle: string;
  setMapStyle: (style: string) => void;
  setDrawMode: (mode: string) => void;
  drawMode: string;
  searchableItems: SearchableItem[];
  onSelectItem: (item: SearchableItem) => void;
  counts: Record<string, number>;
  hidden: Record<ItemLayer, Set<number>>;
  setHidden: Record<ItemLayer, SetOfIds>;
  allIds: Record<ItemLayer, number[]>;
  toggles: Partial<Record<'kaek' | 'uspace', [boolean, (v: boolean) => void]>>;
  weather?: [string, (v: string) => void];
  refresh: Partial<Record<ProposalKind, () => void>>;
  /** A batch proposal can create a task set, which no per-kind refresh covers. */
  refreshTaskSets?: () => void;
  groupName?: string | null;
  selection?: { type: string; id: number; name?: string } | null;
  ghosts: GhostFeature[];
  setGhosts: (ghosts: GhostFeature[]) => void;
  openMarketplace?: () => void;
}

const MAX_NEARBY = 40;
const MAP_STYLE_KEYS = ['dark', 'street', 'positron', 'satellite', 'none'];

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function centroidOf(item: SearchableItem): { lat: number; lng: number } | null {
  if (item.latitude !== undefined && item.longitude !== undefined) {
    return { lat: item.latitude, lng: item.longitude };
  }
  if (item.coordinates && item.coordinates.length > 0) {
    const sum = item.coordinates.reduce(
      (acc, [lng, lat]) => ({ lng: acc.lng + lng, lat: acc.lat + lat }), { lng: 0, lat: 0 });
    return { lat: sum.lat / item.coordinates.length, lng: sum.lng / item.coordinates.length };
  }
  return null;
}

/**
 * Assembles everything the assistant panel needs from App.tsx into a single object, so App.tsx
 * gains one hook call and one prop rather than thirty.
 *
 * The context snapshot deliberately sends counts plus only the ~40 items nearest the viewport,
 * never the full item list: a deployment with a thousand cadastral parcels would otherwise cost
 * 60-100k tokens per turn. Everything else the assistant needs it queries from the backend under
 * the user's own scope, which also keeps answers correct when these cached lists are stale.
 */
export function useAssistantBridge(options: AssistantBridgeOptions): AssistantBridge {
  const {
    getAuthHeaders, mapRef, scope, mapStyle, setMapStyle, setDrawMode, drawMode,
    searchableItems, onSelectItem, counts, hidden, setHidden, allIds, toggles, weather,
    refresh, groupName, selection, setGhosts,
  } = options;

  const [status, setStatus] = useState<AssistantStatus | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let cancelled = false;
    fetchStatus(getAuthHeaders)
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const getMap = useCallback((): maplibregl.Map | null => {
    const ref = mapRef.current as { getMap?: () => maplibregl.Map } | null;
    return ref?.getMap ? ref.getMap() : null;
  }, [mapRef]);

  const flyTo = useCallback((lng: number, lat: number, zoom: number) => {
    getMap()?.flyTo({ center: [lng, lat], zoom, duration: 1000, essential: true });
  }, [getMap]);

  const fitBounds = useCallback((bbox: number[], padding: number) => {
    if (!bbox || bbox.length !== 4) return;
    const [minLng, minLat, maxLng, maxLat] = bbox;
    const map = getMap();
    if (!map) return;
    if (minLng === maxLng && minLat === maxLat) {
      map.flyTo({ center: [minLng, minLat], zoom: 16, duration: 1000, essential: true });
      return;
    }
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding, duration: 1000, essential: true });
  }, [getMap]);

  // Everything below reads through optionsRef and keeps empty dep arrays on purpose: App.tsx
  // rebuilds handleSearchSelect (and the centerOn* helpers it closes over) on every render, so
  // depending on them directly would churn the bridge identity every frame — which in turn would
  // re-run any consumer effect keyed on `bridge`.
  const selectItem = useCallback((type: SelectableType, id: number) => {
    const searchType: SearchItemType = type === 'task_set' ? 'task-set' : type;
    const current = optionsRef.current;
    const item = current.searchableItems.find(
      (candidate) => candidate.type === searchType && Number(candidate.id) === id);
    if (item) current.onSelectItem(item);
  }, []);

  const setLayerVisibility = useCallback(
    (layer: LayerName, visible: boolean, itemIds: number[] | null) => {
      const current = optionsRef.current;
      if ((ITEM_LAYERS as string[]).includes(layer)) {
        const key = layer as ItemLayer;
        const setter = current.setHidden[key];
        if (!setter) return;
        if (itemIds && itemIds.length > 0) {
          setter((prev) => {
            const next = new Set(prev);
            itemIds.forEach((id) => (visible ? next.delete(id) : next.add(id)));
            return next;
          });
        } else {
          setter(visible ? new Set<number>() : new Set<number>(current.allIds[key] || []));
        }
        return;
      }
      if (layer === 'kaek' || layer === 'uspace') {
        current.toggles[layer]?.[1](visible);
        return;
      }
      if (layer === 'weather' && current.weather) {
        current.weather[1](visible ? 'radar' : 'off');
      }
      // incidents and missions have no dedicated visibility toggle today — ignore rather than throw.
    }, []);

  const target: ActionTarget = useMemo(() => ({
    flyTo,
    fitBounds,
    selectItem,
    setLayerVisibility,
    setMapStyle: (style: string) => {
      if (MAP_STYLE_KEYS.includes(style)) optionsRef.current.setMapStyle(style);
    },
    setDrawMode: (mode: string) => optionsRef.current.setDrawMode(mode),
    setGhosts: (ghosts) => optionsRef.current.setGhosts(ghosts),
  }), [flyTo, fitBounds, selectItem, setLayerVisibility]);

  const applyAction = useCallback((action: AssistantAction) => {
    applyActionPure(action, target);
  }, [target]);

  const buildContext = useCallback((): MapContextSnapshot => {
    const current = optionsRef.current;
    const map = getMap();
    const snapshot: MapContextSnapshot = { scope: current.scope };

    if (map) {
      const center = map.getCenter();
      const bounds = map.getBounds();
      snapshot.viewport = {
        center: { lat: Number(center.lat.toFixed(6)), lng: Number(center.lng.toFixed(6)) },
        zoom: Number(map.getZoom().toFixed(2)),
        bbox: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
          .map((v) => Number(v.toFixed(6))),
      };
    }

    snapshot.map_style = current.mapStyle;
    snapshot.draw_mode = current.drawMode;
    snapshot.counts = current.counts;
    if (current.groupName) snapshot.group_name = current.groupName;
    if (current.selection) snapshot.selection = current.selection;

    const hiddenLayers: string[] = [];
    ITEM_LAYERS.forEach((layer) => {
      const hiddenIds = current.hidden[layer];
      const total = (current.allIds[layer] || []).length;
      if (total > 0 && hiddenIds && hiddenIds.size >= total) hiddenLayers.push(layer);
    });
    if (current.toggles.kaek && !current.toggles.kaek[0]) hiddenLayers.push('kaek');
    if (current.toggles.uspace && !current.toggles.uspace[0]) hiddenLayers.push('uspace');
    if (hiddenLayers.length) snapshot.hidden_layers = hiddenLayers;

    // The ~40 nearest items to the current view. Cadastral parcels are intentionally absent —
    // they are reference data, they can number in the thousands, and their count is in `counts`.
    const centerPos = snapshot.viewport?.center;
    const positioned = current.searchableItems
      .map((item) => ({ item, pos: centroidOf(item) }))
      .filter((entry): entry is { item: SearchableItem; pos: { lat: number; lng: number } } =>
        entry.pos !== null);

    if (centerPos) {
      positioned.sort((a, b) =>
        haversineKm(centerPos.lat, centerPos.lng, a.pos.lat, a.pos.lng)
        - haversineKm(centerPos.lat, centerPos.lng, b.pos.lat, b.pos.lng));
    }

    snapshot.nearby = positioned.slice(0, MAX_NEARBY).map(({ item, pos }) => ({
      t: item.type === 'task-set' ? 'task_set' : item.type,
      id: Number(item.id),
      name: item.name,
      lat: Number(pos.lat.toFixed(6)),
      lng: Number(pos.lng.toFixed(6)),
    }));

    return snapshot;
  }, [getMap]);

  const refreshAfterCreate = useCallback((kind: ProposalKind) => {
    optionsRef.current.refresh[kind]?.();
  }, []);

  const refreshTaskSets = useCallback(() => {
    optionsRef.current.refreshTaskSets?.();
  }, []);

  const setGhostsStable = useCallback((ghosts: GhostFeature[]) => {
    optionsRef.current.setGhosts(ghosts);
  }, []);

  const openMarketplace = useCallback(() => {
    optionsRef.current.openMarketplace?.();
  }, []);

  return useMemo(() => ({
    enabled: Boolean(status?.enabled),
    status,
    scope,
    buildContext,
    applyAction,
    setGhosts: setGhostsStable,
    refreshAfterCreate,
    refreshTaskSets,
    flyToBbox: fitBounds,
    openMarketplace,
  }), [status, scope, buildContext, applyAction, setGhostsStable, refreshAfterCreate,
    refreshTaskSets, fitBounds, openMarketplace]);
}
